<style scoped>
/* The H3 layers section of the sidebar: the district's saved layers as the same
   visibility rows the pane's other layers use (a checkbox, a swatch, the name),
   with a one-line summary of each layer's variables so a name is never the only
   thing to go on, and a pencil that opens the editor. The `.control-pane-*`
   names are the pane's own (see ToolsPane.vue) and are re-declared here because
   a scoped rule never reaches into another component's markup. */
.h3-tray {
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid #f1f3f5;
}

.h3-tray-head {
	display: flex;
	align-items: center;
	gap: 6px;
}

.control-pane-title {
	font-weight: 600;
	margin-bottom: 6px;
}

.h3-tray-head .control-pane-title {
	flex: 1 1 auto;
	margin-bottom: 0;
}

.control-pane-row {
	display: flex;
	align-items: center;
	gap: 6px;
	line-height: 1.6;
	margin-top: 2px;
}

/* The visibility rows are labels wrapping a checkbox: clicking anywhere on the
   row toggles it, so the whole line is a pointer target. */
label.control-pane-row {
	cursor: pointer;
}

.control-pane-checkbox {
	margin: 0;
	flex-shrink: 0;
}

/* A hidden row dims its swatch and label; the checkbox keeps full strength, so
   the control to bring the layer back stays obvious. */
.control-pane-row--hidden .control-pane-swatch,
.control-pane-row--hidden .control-pane-label {
	opacity: 0.4;
}

.control-pane-swatch {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid #fff;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
	flex-shrink: 0;
}

/* The name and the variable summary on their own lines, so a long definition
   reads rather than being cut off by the name's own width. */
.control-pane-label {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.h3-tray-name {
	font-weight: 600;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.h3-tray-summary {
	color: #6c757d;
	font-size: 0.6875rem;
	line-height: 1.3;
}

.control-pane-btn {
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 8px;
	font-size: 0.75rem;
	line-height: 1.4;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.control-pane-btn:hover {
	background: #f1f3f5;
}

.control-pane-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

/* The pencil: the one thing that stays when a layer is saved, so it is a
   button of its own on the row rather than a mode of the row. */
.h3-tray-edit {
	flex: 0 0 auto;
	padding: 2px 6px;
}

.h3-tray-note {
	margin: 4px 0 0;
	color: #6c757d;
	font-size: 0.75rem;
	line-height: 1.35;
}

.h3-tray-note--warn {
	color: #b45309;
}

.h3-tray-retry {
	margin-left: 4px;
}
</style>

<template>
	<div class="h3-tray">
		<div class="h3-tray-head">
			<div class="control-pane-title">H3 layers</div>
			<button
				type="button"
				class="control-pane-btn"
				:disabled="loading"
				title="Make a new H3 layer for this district"
				@click="emit('create')"
			>
				+ New
			</button>
		</div>

		<!-- The three states the list can be in, each named: a count of zero is a
		     finding ("nobody has saved one yet"), not an empty box. -->
		<p v-if="loading" class="h3-tray-note">
			Loading this district's H3 layers…
		</p>
		<p v-else-if="error" class="h3-tray-note h3-tray-note--warn">
			{{ error }}
			<button
				type="button"
				class="control-pane-btn h3-tray-retry"
				title="Ask for the district's H3 layers again"
				@click="emit('retry')"
			>
				Retry
			</button>
		</p>
		<p v-else-if="layers.length === 0" class="h3-tray-note">
			No saved H3 layers yet.
		</p>

		<template v-else>
			<label
				v-for="layer in layers"
				:key="layer.id"
				class="control-pane-row"
				:class="{ 'control-pane-row--hidden': hidden.includes(layer.id) }"
			>
				<input
					type="checkbox"
					class="control-pane-checkbox"
					:checked="!hidden.includes(layer.id)"
					@change="emit('toggle', layer.id)"
				/>
				<span
					class="control-pane-swatch"
					:style="{ background: swatch(layer) }"
				></span>
				<span class="control-pane-label">
					<span class="h3-tray-name" :title="layer.name">{{ layer.name }}</span>
					<span class="h3-tray-summary" :title="summary(layer)">{{
						summary(layer)
					}}</span>
				</span>
				<!-- The click must not reach the label: on a label row, a click
				     anywhere toggles the layer, and this button edits instead. -->
				<button
					type="button"
					class="control-pane-btn h3-tray-edit"
					:title="`Edit “${layer.name}”`"
					@click.prevent.stop="emit('edit', layer)"
				>
					<i class="bi bi-pencil-square"></i>
					<span class="visually-hidden">Edit {{ layer.name }}</span>
				</button>
			</label>
		</template>
	</div>
</template>

<script setup lang="ts">
// The district's saved H3 layers, as one section of the layer list. It is
// presentational: the view owns the fetch, the store and the per-user
// visibility, and hands them down as props. Every layer's variables are named
// in the row — a name alone would leave the operator to remember what a layer
// draws — and a definition with no variables says so rather than rendering an
// empty row.
import { h3MetricUnitLabel, h3VariableLabel } from "@/lib/h3/layer";
import type {
	LabH3Unit,
	MapReviewH3Layer,
	MapReviewH3Variable,
} from "@/type/api";

interface Props {
	/** The district's saved H3 layers, in the order the API returned them. */
	layers: MapReviewH3Layer[];
	/** The ids the operator has switched off (per-user prefs, never the DB). */
	hidden: number[];
	loading: boolean;
	/** The last load's failure, already masked by the view's handler. */
	error: string;
	/** The district's live quantity units, for the summary's unit words. */
	units: LabH3Unit[];
	/** The district's resolved area unit, for the area measure's own word. */
	areaUnit: string;
}

interface Emits {
	toggle: [id: number];
	edit: [layer: MapReviewH3Layer];
	create: [];
	retry: [];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

/** One variable as a sentence: what it counts and in what unit. The count's own
 * "records" word is dropped — a count of records IS the number, and the lab's
 * own legend drops it too. */
function variableSentence(
	variable: MapReviewH3Variable,
	index: number,
): string {
	const parts = [h3VariableLabel(variable)];
	if (variable.metric !== "records") {
		const unit = h3MetricUnitLabel(variable, props.areaUnit);
		if (unit) parts.push(unit);
	}
	// The third variable is drawn as the outline, not as a third colour, so the
	// row says which of the variables is the boundary.
	if (index === 2) parts.push("outline");
	return parts.join(" · ");
}

/** What the layer draws, in one line. A definition with no variables draws
 * nothing, and says so rather than leaving a blank row. */
function summary(layer: MapReviewH3Layer): string {
	const variables = layer.definition?.variables ?? [];
	if (variables.length === 0) return "no variables — this layer draws nothing";
	return variables.map(variableSentence).join(" + ");
}

/** The row's swatch is the first variable's colour — the colour the fill is
 * keyed on at a glance. A definition with no variables has no colour to show,
 * so the swatch falls back to the pane's muted grey rather than to nothing. */
function swatch(layer: MapReviewH3Layer): string {
	return layer.definition?.variables?.[0]?.color ?? "#adb5bd";
}
</script>
